import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import HeaderSection from "../components/header-section"
import Sidebar from "../features/sidebar/components/sidebar"
import { unifiedLearningApi, DashboardMeasure, JournalEntry, PromptGroup } from "../services/unifiedLearningApi"
import { useGetOrganizationResultsAccessStatusQuery } from "../app/services/meseeknaApiV2"
import { useTypedSelector, RootState } from "../app/store"
import { useOrganizationRecovery } from "../hooks/useOrganizationRecovery"

// LocalStorage keys for persisting focus measure and start date
const FOCUS_MEASURE_KEY = "microlearning_focus_measure"
const FOCUS_START_DATE_KEY = "microlearning_focus_start_date"

// Get saved focus measure from localStorage
const getSavedFocusMeasure = (): number | null => {
  try {
    const saved = localStorage.getItem(FOCUS_MEASURE_KEY)
    return saved ? parseInt(saved, 10) : null
  } catch {
    return null
  }
}

// Get saved focus start date from localStorage
const getSavedFocusStartDate = (): string | null => {
  try {
    return localStorage.getItem(FOCUS_START_DATE_KEY)
  } catch {
    return null
  }
}

// Save focus measure and start date to localStorage
const saveFocusMeasure = (measureId: number, startDate?: string) => {
  try {
    localStorage.setItem(FOCUS_MEASURE_KEY, measureId.toString())
    // Only set start date if it's a new selection (not already set) or if provided
    if (startDate) {
      localStorage.setItem(FOCUS_START_DATE_KEY, startDate)
    } else if (!localStorage.getItem(FOCUS_START_DATE_KEY)) {
      localStorage.setItem(FOCUS_START_DATE_KEY, new Date().toISOString())
    }
  } catch {
    console.warn("Failed to save focus measure to localStorage")
  }
}

// Calculate which week is time-unlocked based on start date (bi-weekly = every 2 weeks)
const calculateTimeUnlockedWeek = (startDateStr: string | null): number => {
  if (!startDateStr) return 1 // Default to week 1 if no start date

  try {
    const startDate = new Date(startDateStr)
    const now = new Date()
    const diffMs = now.getTime() - startDate.getTime()
    const diffWeeks = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000)) // 1 week intervals

    // Week 1 is available immediately, then new week unlocks each week
    return Math.max(1, diffWeeks + 1)
  } catch {
    return 1
  }
}

// Measure icon mapping by name - more reliable than ID-based mapping
// Maps measure names (lowercase) to icon filenames
const MEASURE_NAME_ICON_MAP: Record<string, string> = {
  "developmental orientation": "1. Developmental Orientation.png",
  "development orientation": "1. Developmental Orientation.png", // alias
  "dependability": "2. Dependability.png",
  "innovation": "3. Innovation.png",
  "creative decisiveness": "4. Creative Decisiveness.png",
  "creative flexibility": "5. Creative Flexibility.png",
  "workplace engagement": "6. Workplace Engagement.png",
  "people centrism": "7. People Centrism.png",
  "communication": "8. Communication.png",
  "empathetic communication": "9. Empathetic Communication.png",
  "collaboration": "10. Collaboration.png",
  "team orientation": "11 Team Orientation.png",
  "conflict approach": "12 Conflit Approach.png",
  "conflict response": "13. Conflict Response.png",
  "conflict management": "14. Conflict Management.png",
  "conflict resolution": "14. Conflict Management.png", // alias
  "emotional resilience": "15. Emotional Resilience.png",
  "breadth of approach": "16. Breadth of Approach.png",
  "resource management": "17. Resource Management.png",
  "crisis preparedness": "18. Crisis Preparedness.png",
  "crisis response": "19. Crisis Response.png",
  "crisis recovery": "20. Crisis Recovery.png",
  "task management": "21. Task Management.png",
  "information management": "22. Information Management.png",
  "strategic quantitative reasoning": "23. Strategic Quantitative Reasoning.png",
  "strategic quantitative thinking": "23. Strategic Quantitative Reasoning.png", // alias
  "strategic thinking": "24. Strategic Thinking.png",
  "advanced strategy": "25. Advanced Strategy.png",
  "productivity": "26. Productivity.png",
  "proactivity": "27. Proactivity.png",
  "initiative": "28. Initiative.png",
  "goal orientation": "29. Goal Orientation.png",
  "goal management": "30. Goal Management.png",
}

// Get icon path for a measure by name
const getMeasureIconPathByName = (measureName: string | undefined): string | null => {
  if (!measureName) return null
  const filename = MEASURE_NAME_ICON_MAP[measureName.toLowerCase()]
  return filename ? `/v3/assets/measures/${filename}` : null
}

// Get tier display label
const getTierDisplayLabel = (tier?: string): string => {
  if (!tier) return 'Not Assessed'
  switch (tier) {
    case 'strongest':
      return 'Super Power'
    case 'strong':
      return 'Strength'
    case 'developing':
      return 'Developing'
    case 'weakest':
      return 'Growth Zone'
    default:
      return tier
  }
}

// Local measure type that extends DashboardMeasure with display-specific fields
type LocalMeasure = DashboardMeasure & {
  // Score and tier may be undefined if user has no assessment data
  score?: number
  tier?: string
  definition: string
}

// Grouped prompts state
type GroupedPromptsState = {
  groups: PromptGroup[]
  totalWeeks: number
}

// Dynamic scenarios interface
type DashboardScenario = {
  id: number
  measureId: number
  measureName: string
  title: string
  story: string
  choices: Array<{
    id: string
    text: string
    feedback: string
    isOptimal: boolean
  }>
}

type DeliveryMethod = "email" | "text" | "portal"
type DeliveryTime = "morning" | "afternoon" | "evening"

const MicrolearningDashboard: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isRecovering } = useOrganizationRecovery()

  // Get organization ID from auth store
  const authStore = useTypedSelector((state: RootState) => state.authSlice)
  const effectiveOrgId = Number(
    authStore.organization?.id || authStore.user?.organizationId || 0,
  )

  // Determine active section from URL path
  const getActiveSection = (): 'overview' | 'journal' | 'scenarios' | 'settings' => {
    const path = location.pathname
    if (path.includes('/microlearning/journal')) return 'journal'
    if (path.includes('/microlearning/scenarios')) return 'scenarios'
    if (path.includes('/microlearning/settings')) return 'settings'
    return 'overview' // Default to overview for /microlearning and /microlearning/overview
  }

  const activeSection = getActiveSection()

  // Check results access status for microlearning
  const { data: accessStatusData, isLoading: isLoadingAccessStatus } =
    useGetOrganizationResultsAccessStatusQuery(effectiveOrgId, {
      skip: !effectiveOrgId || isRecovering,
    })

  const accessStatus = accessStatusData?.data
  const hasResultsAccess = accessStatus?.hasAccess ?? true // Default to true to avoid blocking if API fails

  // State management - Dynamic data from API
  const [measures, setMeasures] = useState<LocalMeasure[]>([])
  const [groupedPrompts, setGroupedPrompts] = useState<GroupedPromptsState>({ groups: [], totalWeeks: 0 })
  const [scenarios, setScenarios] = useState<DashboardScenario[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMeasureContent, setLoadingMeasureContent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Journal entries state
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([])
  const [loadingEntries, setLoadingEntries] = useState(false)

  // Journal writing state
  const [activePromptId, setActivePromptId] = useState<number | null>(null)
  const [journalText, setJournalText] = useState("")
  const [savingEntry, setSavingEntry] = useState(false)
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  // Week pagination state - ONE group of prompts per week
  const [currentWeek, setCurrentWeek] = useState(1)

  // Calculate week completion status and unlocked weeks
  const weekCompletionStatus = useMemo(() => {
    const completedPromptIds = new Set(journalEntries.map(e => e.promptId))
    const weekStatus: { weekNumber: number; isComplete: boolean; completedCount: number; totalCount: number }[] = []

    groupedPrompts.groups.forEach(group => {
      const allPromptIds = [group.mainPrompt.id, ...group.subPrompts.map(s => s.id)]
      const completedCount = allPromptIds.filter(id => completedPromptIds.has(id)).length
      const totalCount = allPromptIds.length
      weekStatus.push({
        weekNumber: group.weekNumber,
        isComplete: completedCount === totalCount && totalCount > 0,
        completedCount,
        totalCount
      })
    })

    return weekStatus
  }, [groupedPrompts.groups, journalEntries])

  // UI State - Single measure focus (initialize from localStorage)
  // Note: These must be declared before the memos that depend on them
  const [selectedMeasure, setSelectedMeasure] = useState<number | null>(() => getSavedFocusMeasure())
  const [focusStartDate, setFocusStartDate] = useState<string | null>(() => getSavedFocusStartDate())

  // Calculate time-based unlocked week (based on when user started their focus)
  const timeUnlockedWeek = useMemo(() => {
    return calculateTimeUnlockedWeek(focusStartDate)
  }, [focusStartDate])

  // Calculate completion-based unlocked week (first incomplete week)
  const completionUnlockedWeek = useMemo(() => {
    // Find the first incomplete week - that's the maximum accessible week
    for (let i = 0; i < weekCompletionStatus.length; i++) {
      if (!weekCompletionStatus[i].isComplete) {
        return weekCompletionStatus[i].weekNumber
      }
    }
    // All weeks complete - return the last week + 1 (allow progression)
    return weekCompletionStatus.length > 0 ? weekCompletionStatus.length + 1 : 1
  }, [weekCompletionStatus])

  // Final unlocked week is the MINIMUM of time-based and completion-based
  // Must complete previous weeks AND wait for time to pass
  const unlockedWeek = useMemo(() => {
    return Math.min(timeUnlockedWeek, completionUnlockedWeek)
  }, [timeUnlockedWeek, completionUnlockedWeek])

  // Check if current week is complete
  const currentWeekStatus = useMemo(() => {
    return weekCompletionStatus.find(w => w.weekNumber === currentWeek)
  }, [weekCompletionStatus, currentWeek])
  const hasInitializedMeasure = useRef(false)
  const [deliverySettings, setDeliverySettings] = useState({
    method: "email" as DeliveryMethod,
    time: "morning" as DeliveryTime,
    frequency: "daily",
    phoneNumber: "",
    countryCode: "+1",
    email: "",
  })
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null)
  const [showFeedback, setShowFeedback] = useState(false)
  const [showMeasureSelection, setShowMeasureSelection] = useState(false)

  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingMeasureId, setPendingMeasureId] = useState<number | null>(null)
  const [measureChangeError, setMeasureChangeError] = useState<string | null>(null)
  const [remainingChanges, setRemainingChanges] = useState<number | null>(null)
  const [isChangingMeasure, setIsChangingMeasure] = useState(false)

  // Delivery preferences state
  const [savingPreferences, setSavingPreferences] = useState(false)
  const [loadingPreferences, setLoadingPreferences] = useState(false)
  const [preferencesMessage, setPreferencesMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  // Current user ID (would come from auth context in real app)
  // const currentUserId = 1 // Commented out as it's not used yet

  // Helper functions
  const getTierColor = (tier: string) => {
    switch (tier) {
      case "strongest": return "text-green-700 bg-green-100 border-green-200"
      case "strong": return "text-blue-700 bg-blue-100 border-blue-200"
      case "developing": return "text-yellow-700 bg-yellow-100 border-yellow-200"
      case "weakest": return "text-red-700 bg-red-100 border-red-200"
      default: return "text-gray-700 bg-gray-100 border-gray-200"
    }
  }

  const getTierLabel = (tier: string) => {
    switch (tier) {
      case "strongest": return "Superpower"
      case "strong": return "Strength"
      case "developing": return "Growth Area"
      case "weakest": return "Growth Zone"
      default: return "Measured"
    }
  }

  // Check if a prompt has been completed
  const isPromptCompleted = useCallback((promptId: number) => {
    return journalEntries.some(entry => entry.promptId === promptId)
  }, [journalEntries])

  // Get the journal entry for a prompt (if it exists)
  const getPromptEntry = useCallback((promptId: number) => {
    return journalEntries.find(entry => entry.promptId === promptId)
  }, [journalEntries])

  // Phone number validation helper
  const isValidPhoneNumber = (phone: string): boolean => {
    // Basic validation: at least 10 digits
    const digitsOnly = phone.replace(/[^\d]/g, '')
    return digitsOnly.length >= 10 && digitsOnly.length <= 15
  }

  // Email validation helper
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const formatPhoneNumber = (phone: string): string => {
    const digits = phone.replace(/[^\d]/g, '')
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    return phone
  }

  const handleChoiceSelect = (choiceId: string) => {
    setSelectedChoice(choiceId)
    setShowFeedback(true)
  }

  const resetScenario = () => {
    setSelectedChoice(null)
    setShowFeedback(false)
  }

  // Handle measure selection with confirmation
  const handleMeasureSelect = useCallback((measureId: number) => {
    // If already has a saved measure, show confirmation
    if (selectedMeasure && selectedMeasure !== measureId) {
      setPendingMeasureId(measureId)
      setShowConfirmDialog(true)
    } else {
      // First time selection or selecting same measure
      setSelectedMeasure(measureId)
      saveFocusMeasure(measureId)
      setShowMeasureSelection(false)
    }
  }, [selectedMeasure])

  // Confirm measure change - calls API with limit check
  const confirmMeasureChange = useCallback(async () => {
    if (!pendingMeasureId) return

    const userId = authStore.user?.id
    if (!userId) {
      setMeasureChangeError('You must be logged in to change your focus measure')
      return
    }

    setIsChangingMeasure(true)
    setMeasureChangeError(null)

    try {
      const result = await unifiedLearningApi.setUserFocusMeasure(Number(userId), pendingMeasureId)

      // Update local state - reset start date when changing measure
      // This ensures the weekly lockout restarts from week 1
      const newStartDate = new Date().toISOString()
      setSelectedMeasure(pendingMeasureId)
      saveFocusMeasure(pendingMeasureId, newStartDate)
      setFocusStartDate(newStartDate)
      setShowMeasureSelection(false)
      setShowConfirmDialog(false)
      setPendingMeasureId(null)

      // Update remaining changes if returned
      if (result.remainingChangesThisYear !== undefined) {
        setRemainingChanges(result.remainingChangesThisYear)
      }
    } catch (error) {
      console.error('Error setting focus measure:', error)
      const err = error as { code?: string; message?: string }
      if (err.code === 'MEASURE_CHANGE_LIMIT_REACHED') {
        setMeasureChangeError(err.message || 'You have reached the maximum focus measure changes for this year.')
      } else {
        setMeasureChangeError('Failed to change focus measure. Please try again.')
      }
    } finally {
      setIsChangingMeasure(false)
    }
  }, [pendingMeasureId, authStore.user?.id])

  // Cancel measure change
  const cancelMeasureChange = useCallback(() => {
    setShowConfirmDialog(false)
    setPendingMeasureId(null)
    setMeasureChangeError(null)
  }, [])

  // Save delivery preferences
  const handleSavePreferences = useCallback(async () => {
    const userId = authStore.user?.id
    if (!userId) {
      setPreferencesMessage({ type: 'error', text: 'You must be logged in to save preferences' })
      return
    }

    // Validate phone number if SMS is selected
    if (deliverySettings.method === 'text' && !isValidPhoneNumber(deliverySettings.phoneNumber)) {
      setPreferencesMessage({ type: 'error', text: 'Please enter a valid phone number for SMS delivery' })
      return
    }

    try {
      setSavingPreferences(true)
      setPreferencesMessage(null)

      // Map UI format to API format
      let deliveryMethod: 'email' | 'sms' | 'both' = 'email'
      if (deliverySettings.method === 'text') deliveryMethod = 'sms'
      else if (deliverySettings.method === 'portal') deliveryMethod = 'email' // Portal defaults to email notifications

      // Map time of day to preferred time
      let preferredTime = '09:00'
      if (deliverySettings.time === 'afternoon') preferredTime = '12:00'
      else if (deliverySettings.time === 'evening') preferredTime = '18:00'

      await unifiedLearningApi.updateDeliveryPreferences({
        userId: Number(userId),
        deliveryMethod,
        frequency: deliverySettings.frequency as 'daily' | 'weekly' | 'biweekly',
        preferredTime,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        phoneNumber: deliverySettings.method === 'text' ? deliverySettings.phoneNumber : undefined,
      })

      setPreferencesMessage({ type: 'success', text: 'Preferences saved successfully!' })

      // Clear success message after 3 seconds
      setTimeout(() => setPreferencesMessage(null), 3000)
    } catch (err) {
      console.error('Error saving preferences:', err)
      setPreferencesMessage({ type: 'error', text: 'Failed to save preferences. Please try again.' })
    } finally {
      setSavingPreferences(false)
    }
  }, [authStore.user?.id, deliverySettings])

  // Handle starting to write a journal entry
  const handleStartWriting = useCallback((promptId: number) => {
    setActivePromptId(promptId)
    setJournalText("")
    setSaveMessage(null)
  }, [])

  // Handle canceling journal writing
  const handleCancelWriting = useCallback(() => {
    setActivePromptId(null)
    setJournalText("")
    setSaveMessage(null)
  }, [])

  // Handle saving journal entry
  const handleSaveJournalEntry = useCallback(async (promptId: number) => {
    const userId = authStore.user?.id
    if (!userId || !selectedMeasure || !journalText.trim()) {
      setSaveMessage({ type: 'error', text: 'Please write something before saving' })
      return
    }

    try {
      setSavingEntry(true)
      setSaveMessage(null)

      await unifiedLearningApi.createJournalEntry({
        userId: Number(userId),
        promptId,
        response: journalText.trim(),
        measureId: selectedMeasure
      })

      setSaveMessage({ type: 'success', text: 'Journal entry saved!' })

      // Reload journal entries
      const entries = await unifiedLearningApi.getJournalEntries({
        userId: Number(userId),
        measureId: selectedMeasure,
        limit: 10
      })
      setJournalEntries(entries)

      // Clear writing state after a short delay
      setTimeout(() => {
        setActivePromptId(null)
        setJournalText("")
        setSaveMessage(null)
      }, 1500)
    } catch (err: unknown) {
      console.error('Error saving journal entry:', err)
      // Extract error message from API response if available
      let errorMessage = 'Failed to save. Please try again.'
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } }
        if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message
        }
      }
      setSaveMessage({ type: 'error', text: errorMessage })
    } finally {
      setSavingEntry(false)
    }
  }, [authStore.user?.id, selectedMeasure, journalText])

  // Load data from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch organization-filtered measures with user scores (max 30)
        const apiMeasures = effectiveOrgId
          ? await unifiedLearningApi.getMeasuresForOrganization(effectiveOrgId)
          : await unifiedLearningApi.getMeasuresWithScores()

        // Transform API measures to local format
        const dashboardMeasures: LocalMeasure[] = apiMeasures.map((measure) => ({
          ...measure,
          definition: measure.description,
        }))

        setMeasures(dashboardMeasures)

        // Only auto-select if no saved measure exists
        // Use ref to prevent multiple initializations
        if (dashboardMeasures.length > 0 && !hasInitializedMeasure.current) {
          hasInitializedMeasure.current = true

          const userId = authStore.user?.id

          // Try to get user's focus measure from API first (server is source of truth)
          if (userId) {
            try {
              const apiFocusMeasure = await unifiedLearningApi.getUserFocusMeasure(Number(userId))
              if (apiFocusMeasure && dashboardMeasures.some(m => m.id === apiFocusMeasure.id)) {
                // Use API focus measure and its createdOn date for weekly lockout
                setSelectedMeasure(apiFocusMeasure.id)
                const apiStartDate = apiFocusMeasure.createdOn || new Date().toISOString()
                saveFocusMeasure(apiFocusMeasure.id, apiStartDate)
                setFocusStartDate(apiStartDate)
                return // Exit early, we have the user's focus from API
              }
            } catch (err) {
              console.warn('Could not fetch focus measure from API, falling back to localStorage:', err)
            }
          }

          // Fallback: Check if saved measure exists and is valid in localStorage
          const savedMeasure = getSavedFocusMeasure()
          const savedMeasureExists = savedMeasure && dashboardMeasures.some(m => m.id === savedMeasure)

          if (savedMeasureExists) {
            // Use saved measure
            setSelectedMeasure(savedMeasure)
          } else {
            // No saved measure - auto-select strongest
            const measuresWithScores = dashboardMeasures.filter(m => m.score !== undefined)
            if (measuresWithScores.length > 0) {
              const strongest = measuresWithScores.reduce((prev, current) =>
                ((current.score ?? 0) > (prev.score ?? 0)) ? current : prev
              )
              setSelectedMeasure(strongest.id)
              saveFocusMeasure(strongest.id)
            } else {
              // No user scores yet, just select the first measure
              setSelectedMeasure(dashboardMeasures[0].id)
              saveFocusMeasure(dashboardMeasures[0].id)
            }
          }
        }

      } catch (err) {
        console.error('Error loading measures:', err)
        setError('Failed to load learning content. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [effectiveOrgId, authStore.user?.id])

  // Load measure-specific content when selected measure changes
  useEffect(() => {
    const loadMeasureContent = async () => {
      if (!selectedMeasure) return

      try {
        setLoadingMeasureContent(true)

        // Load grouped prompts for selected measure
        const groupedData = await unifiedLearningApi.getGroupedPrompts({ measureId: selectedMeasure })
        if (groupedData.prompts) {
          const groups = Array.isArray(groupedData.prompts) ? groupedData.prompts : [groupedData.prompts]
          setGroupedPrompts({ groups, totalWeeks: groupedData.totalWeeks })
        } else {
          setGroupedPrompts({ groups: [], totalWeeks: 0 })
        }

        // Load scenarios for selected measure
        const scenarioData = await unifiedLearningApi.getFormattedScenarios(selectedMeasure)
        setScenarios(scenarioData)

        // Reset scenario selection state when measure changes
        setSelectedChoice(null)
        setShowFeedback(false)

      } catch (err) {
        console.error('Error loading measure content:', err)
      } finally {
        setLoadingMeasureContent(false)
      }
    }

    loadMeasureContent()
  }, [selectedMeasure])

  // Load journal entries
  useEffect(() => {
    const loadJournalEntries = async () => {
      const userId = authStore.user?.id
      if (!userId) return

      try {
        setLoadingEntries(true)
        const entries = await unifiedLearningApi.getJournalEntries({
          userId: Number(userId),
          measureId: selectedMeasure || undefined,
          limit: 10
        })
        setJournalEntries(entries)
      } catch (err) {
        console.error('Error loading journal entries:', err)
      } finally {
        setLoadingEntries(false)
      }
    }

    loadJournalEntries()
  }, [authStore.user?.id, selectedMeasure])

  // Load delivery preferences on mount or user change
  useEffect(() => {
    const loadDeliveryPreferences = async () => {
      const userId = authStore.user?.id
      if (!userId) {
        // Reset phone number when user logs out to prevent cross-user leakage
        setDeliverySettings(prev => ({
          ...prev,
          phoneNumber: "",
          countryCode: "+1"
        }))
        return
      }

      // Reset phone number when loading preferences for a new user
      setDeliverySettings(prev => ({
        ...prev,
        phoneNumber: "",
        countryCode: "+1"
      }))

      try {
        setLoadingPreferences(true)
        const prefs = await unifiedLearningApi.getDeliveryPreferences(Number(userId))

        // Map API format to UI format
        let method: DeliveryMethod = 'email'
        if (prefs.deliveryMethod === 'sms') method = 'text'
        else if (prefs.deliveryMethod === 'both') method = 'email' // Default to email for "both"
        else if (prefs.deliveryMethod === 'email') method = 'email'

        // Map preferred time to time of day
        let time: DeliveryTime = 'morning'
        if (prefs.preferredTime) {
          const hour = parseInt(prefs.preferredTime.split(':')[0], 10)
          if (hour >= 12 && hour < 17) time = 'afternoon'
          else if (hour >= 17) time = 'evening'
        }

        setDeliverySettings(prev => ({
          ...prev,
          method,
          time,
          frequency: prefs.frequency || 'daily',
          // Initialize email from user profile if available
          email: authStore.user?.email || prev.email
        }))

        // Also try to load phone number if method is SMS
        if (prefs.deliveryMethod === 'sms' || prefs.deliveryMethod === 'both') {
          try {
            const phoneData = await unifiedLearningApi.getUserPhoneNumber(Number(userId))
            if (phoneData.phoneNumber) {
              // Parse phone number (assume it includes country code)
              const phone = phoneData.phoneNumber
              // Simple parsing - take last 10 digits as number, rest as country code
              const digits = phone.replace(/[^\d]/g, '')
              if (digits.length > 10) {
                setDeliverySettings(prev => ({
                  ...prev,
                  countryCode: '+' + digits.slice(0, digits.length - 10),
                  phoneNumber: digits.slice(-10)
                }))
              } else {
                setDeliverySettings(prev => ({
                  ...prev,
                  phoneNumber: digits
                }))
              }
            }
          } catch (phoneErr) {
            console.warn('Could not load phone number:', phoneErr)
          }
        }
      } catch (err) {
        // Preferences might not exist yet, that's okay
        console.warn('Could not load delivery preferences:', err)
      } finally {
        setLoadingPreferences(false)
      }
    }

    loadDeliveryPreferences()
  }, [authStore.user?.id])

  const selectedMeasureData = measures.find(m => m.id === selectedMeasure)

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Single Measure Focus Header */}
      {selectedMeasure && selectedMeasureData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4 mb-4">
            {getMeasureIconPathByName(selectedMeasureData?.name) ? (
              <img
                src={getMeasureIconPathByName(selectedMeasureData?.name)!}
                alt={selectedMeasureData.name}
                className="w-16 h-16 object-contain"
              />
            ) : (
              <div
                className="w-16 h-16 shrink-0 rounded-full border-4 border-gray-100 shadow-sm flex items-center justify-center bg-white"
              />
            )}
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                Your Focus: {selectedMeasureData.name}
              </h2>
              <p className="text-gray-600 font-medium">
                {getTierDisplayLabel(selectedMeasureData.tier)} • {selectedMeasureData.definition}
              </p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 mb-3 leading-relaxed">
              <strong>Your commitment:</strong> Focus deeply on developing {selectedMeasureData.name.toLowerCase()} throughout the year.
              All your journal prompts, scenarios, and learning materials are tailored to help you master this competency.
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                <span>Daily prompts for {selectedMeasureData.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span>Weekly reflection practice</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Measure Selection - Only show if no measure selected */}
      {!selectedMeasure && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">
            Choose Your Learning Focus
          </h2>
          <p className="text-gray-600 mb-4 leading-relaxed">
            Select one measure to focus on this year. Deep, focused development on a single competency
            leads to more meaningful and lasting improvement than trying to improve multiple areas at once.
          </p>
        </div>
      )}

      {/* Measure Selection/Management */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {selectedMeasure ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Your Focus Measure
              </h3>
              <button
                onClick={() => setShowMeasureSelection(!showMeasureSelection)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                {showMeasureSelection ? 'Hide Options' : 'Change Focus'}
              </button>
            </div>

            {/* Current Selection Highlight */}
            <div className="p-4 rounded-lg border-2 border-blue-500 bg-blue-50 shadow-md mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {selectedMeasure && getMeasureIconPathByName(selectedMeasureData?.name) ? (
                    <img
                      src={getMeasureIconPathByName(selectedMeasureData?.name)!}
                      alt={selectedMeasureData?.name}
                      className="w-12 h-12 object-contain"
                    />
                  ) : (
                    <div
                      className="w-12 h-12 shrink-0 rounded-full border-3 border-white shadow-sm flex items-center justify-center bg-white"
                    />
                  )}
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">{selectedMeasureData?.name}</h4>
                    <p className="text-gray-600 text-sm">{selectedMeasureData?.definition}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getTierColor(selectedMeasureData?.tier || '')}`}>
                  {getTierLabel(selectedMeasureData?.tier || '')}
                </span>
              </div>
            </div>

            {/* Development Journey */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Your Development Journey</h4>
              <p className="text-gray-700 text-sm mb-3">
                Every learning activity this year is designed to strengthen your {selectedMeasureData?.name.toLowerCase()} skills.
                Consistent weekly practice in this one area will create lasting improvement.
              </p>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-green-700">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  <span>Focused learning path</span>
                </div>
                <div className="flex items-center gap-2 text-blue-700">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  <span>Personalized content</span>
                </div>
                <div className="flex items-center gap-2 text-purple-700">
                  <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                  <span>Measurable progress</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Choose Your Annual Focus
            </h3>
            <p className="text-sm text-gray-500 mb-4">Select one measure to develop this year</p>
          </>
        )}

        {/* Show all measures for selection */}
        {(showMeasureSelection || !selectedMeasure) && (
          <div className="grid gap-4 mt-4">
            <div className="text-sm text-gray-600 mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="font-medium text-blue-800 mb-1">Focus Strategy</p>
              <p>Research shows that focusing on one competency at a time leads to 3X better results than trying to improve multiple areas simultaneously.</p>
            </div>
            {measures.map((measure) => (
              <div
                key={measure.id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                  selectedMeasure === measure.id
                    ? "border-blue-500 bg-blue-50 shadow-md"
                    : measure.tier === "strongest"
                    ? "border-green-300 bg-green-50 hover:border-green-400"
                    : measure.tier === "weakest"
                    ? "border-orange-200 bg-orange-50 hover:border-orange-300"
                    : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                }`}
                onClick={() => handleMeasureSelect(measure.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 min-w-0">
                    {getMeasureIconPathByName(measure.name) ? (
                      <img
                        src={getMeasureIconPathByName(measure.name)!}
                        alt={measure.name}
                        className="w-12 h-12 object-contain shrink-0"
                      />
                    ) : (
                      <div
                        className="w-12 h-12 shrink-0 rounded-full border-3 border-white shadow-sm flex items-center justify-center bg-white"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold text-gray-900 text-lg">{measure.name}</h4>
                      <p className="text-gray-600 text-sm">{measure.definition}</p>
                      {measure.tier === "strongest" && (
                        <p className="text-green-700 text-xs font-medium mt-1">Recommended: Build on your Super Power</p>
                      )}
                      {measure.tier === "weakest" && (
                        <p className="text-orange-700 text-xs font-medium mt-1">Alternative: Transform your Growth Zone</p>
                      )}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border shrink-0 ${getTierColor(measure.tier ?? '')}`}>
                    {getTierLabel(measure.tier ?? '')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Focus Measure Progress */}
      {selectedMeasure && selectedMeasureData && (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
            {getMeasureIconPathByName(selectedMeasureData?.name) ? (
              <img
                src={getMeasureIconPathByName(selectedMeasureData?.name)!}
                alt={selectedMeasureData.name}
                className="w-12 h-12 mx-auto mb-3 object-contain"
              />
            ) : (
              <div className="w-10 h-10 mx-auto mb-3 rounded-full flex items-center justify-center bg-white border border-gray-200">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            )}
            <div className="text-xl font-bold" style={{ color: selectedMeasureData.color }}>
              {getTierDisplayLabel(selectedMeasureData.tier)}
            </div>
            <div className="text-gray-500 text-sm">Current Level</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
            <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-blue-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="text-2xl font-bold text-blue-600">365</div>
            <div className="text-gray-500 text-sm">Days to Master</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
            <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-orange-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="text-2xl font-bold text-orange-600">0</div>
            <div className="text-gray-500 text-sm">Day Streak</div>
          </div>
        </div>
      )}
    </div>
  )

  const renderJournalPrompts = () => {
    // Get current week's prompt group
    const currentGroup = groupedPrompts.groups.find(g => g.weekNumber === currentWeek)
    const totalWeeks = groupedPrompts.totalWeeks || groupedPrompts.groups.length

    return (
    <div className="space-y-6">
      {/* Focus Banner */}
      {selectedMeasure && selectedMeasureData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            {getMeasureIconPathByName(selectedMeasureData?.name) ? (
              <img
                src={getMeasureIconPathByName(selectedMeasureData?.name)!}
                alt={selectedMeasureData.name}
                className="w-10 h-10 object-contain"
              />
            ) : (
              <div
                className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center bg-white border border-gray-200"
              />
            )}
            <div>
              <div className="font-semibold text-gray-900">All prompts focus on: {selectedMeasureData.name}</div>
              <div className="text-sm text-gray-500">Targeted questions to develop your chosen competency</div>
            </div>
          </div>
        </div>
      )}

      {/* Journal Prompts - Organized by Week with Groups */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900">{selectedMeasureData?.name || 'Focus Measure'} Prompts</h3>
          {totalWeeks > 0 && (
            <div className="text-sm text-gray-500">
              Week {currentWeek} of {totalWeeks}
            </div>
          )}
        </div>

        {/* Week Navigation */}
        {totalWeeks > 1 && (
          <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
            <button
              onClick={() => setCurrentWeek(w => Math.max(1, w - 1))}
              disabled={currentWeek === 1}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                currentWeek === 1
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
              }`}
            >
              ← Previous Week
            </button>
            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, totalWeeks) }, (_, i) => {
                const weekNum = i + 1
                const isLocked = weekNum > unlockedWeek
                const weekStatus = weekCompletionStatus.find(w => w.weekNumber === weekNum)
                const isComplete = weekStatus?.isComplete ?? false
                return (
                  <button
                    key={weekNum}
                    onClick={() => !isLocked && setCurrentWeek(weekNum)}
                    disabled={isLocked}
                    className={`w-8 h-8 rounded-full text-sm font-medium transition-colors relative ${
                      isLocked
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                        : currentWeek === weekNum
                        ? "bg-blue-600 text-white"
                        : isComplete
                        ? "bg-green-100 text-green-700 border border-green-300"
                        : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-300"
                    }`}
                    title={isLocked ? "Complete current week to unlock" : isComplete ? "Week completed!" : `Week ${weekNum}`}
                  >
                    {isLocked ? (
                      <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    ) : isComplete ? (
                      <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      weekNum
                    )}
                  </button>
                )
              })}
              {totalWeeks > 5 && (
                <span className="text-gray-500 text-sm">...</span>
              )}
            </div>
            <button
              onClick={() => setCurrentWeek(w => Math.min(unlockedWeek, w + 1))}
              disabled={currentWeek >= unlockedWeek}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                currentWeek >= unlockedWeek
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
              }`}
            >
              Next Week →
            </button>
          </div>
        )}

        <div className="space-y-4">
          {!selectedMeasure ? (
            // No measure selected
            <div className="text-center p-8 text-gray-500">
              <p className="font-medium mb-2">No focus measure selected</p>
              <p className="text-sm mb-4">Select a focus measure first to see personalized journal prompts</p>
              <button
                onClick={() => navigate('/microlearning/overview')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Choose Your Focus
              </button>
            </div>
          ) : loadingMeasureContent ? (
            // Loading prompts
            <div className="text-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading prompts for {selectedMeasureData?.name}...</p>
            </div>
          ) : currentGroup ? (
            // Show grouped prompts for current week (main prompt + sub-prompts)
            <div className="space-y-4">
              {/* Main Prompt */}
              {(() => {
                const mainPromptCompleted = isPromptCompleted(currentGroup.mainPrompt.id)
                return (
              <div className={`border-2 rounded-lg transition-all ${
                activePromptId === currentGroup.mainPrompt.id
                  ? "border-blue-500 bg-blue-50 shadow-md"
                  : mainPromptCompleted
                  ? "border-green-300 bg-gradient-to-r from-green-50 to-white"
                  : "border-blue-200 bg-gradient-to-r from-blue-50 to-white hover:shadow-sm"
              }`}>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full text-white text-sm font-bold ${
                      mainPromptCompleted ? "bg-green-500" : "bg-blue-600"
                    }`}>
                      {mainPromptCompleted ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        `W${currentWeek}`
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-lg">Week {currentWeek} - Main Prompt</div>
                      <div className={`text-sm capitalize font-medium ${mainPromptCompleted ? "text-green-600" : "text-blue-600"}`}>{currentGroup.mainPrompt.category}</div>
                    </div>
                  </div>
                  <p className="text-gray-800 leading-relaxed text-lg mb-4">{currentGroup.mainPrompt.promptText}</p>

                  {activePromptId === currentGroup.mainPrompt.id ? (
                    // Writing mode
                    <div className="space-y-3">
                      <textarea
                        value={journalText}
                        onChange={(e) => setJournalText(e.target.value)}
                        placeholder="Write your reflection here..."
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[150px] text-gray-700"
                        autoFocus
                      />
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                          {journalText.length} characters
                        </div>
                        <div className="flex items-center gap-3">
                          {saveMessage && (
                            <span className={`text-sm ${saveMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                              {saveMessage.text}
                            </span>
                          )}
                          <button
                            onClick={handleCancelWriting}
                            className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleSaveJournalEntry(currentGroup.mainPrompt.id)}
                            disabled={savingEntry || !journalText.trim()}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              savingEntry || !journalText.trim()
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                            }`}
                          >
                            {savingEntry ? "Saving..." : "Save Entry"}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : isPromptCompleted(currentGroup.mainPrompt.id) ? (
                    // Completed state - show the entry
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-green-600 mb-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm font-medium">Completed</span>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-gray-700 text-sm whitespace-pre-wrap">
                          {getPromptEntry(currentGroup.mainPrompt.id)?.response || 'Entry saved'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleStartWriting(currentGroup.mainPrompt.id)}
                        className="text-gray-500 hover:text-gray-700 text-xs font-medium flex items-center gap-1"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        Edit response
                      </button>
                    </div>
                  ) : (
                    // View mode - not yet completed
                    <button
                      onClick={() => handleStartWriting(currentGroup.mainPrompt.id)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      Reflect on this prompt
                    </button>
                  )}
                </div>
              </div>
              )})()}

              {/* Sub-Prompts */}
              {currentGroup.subPrompts.length > 0 && (
                <div className="ml-4 border-l-4 border-blue-200 pl-4 space-y-3">
                  <div className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                    Follow-up Questions ({currentGroup.subPrompts.length})
                  </div>
                  {currentGroup.subPrompts.map((subPrompt, index) => {
                    const subPromptCompleted = isPromptCompleted(subPrompt.id)
                    return (
                    <div key={subPrompt.id} className={`border rounded-lg transition-all ${
                      activePromptId === subPrompt.id
                        ? "border-blue-400 bg-blue-50 shadow-sm"
                        : subPromptCompleted
                        ? "border-green-200 bg-green-50"
                        : "border-gray-200 bg-gray-50 hover:shadow-sm"
                    }`}>
                      <div className="p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                            subPromptCompleted
                              ? "bg-green-500 text-white"
                              : "bg-gray-200 text-gray-600"
                          }`}>
                            {subPromptCompleted ? (
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              index + 1
                            )}
                          </div>
                          <div className="text-xs text-gray-500 capitalize">{subPrompt.category}</div>
                          {subPromptCompleted && (
                            <span className="text-xs text-green-600 font-medium">Completed</span>
                          )}
                        </div>
                        <p className="text-gray-700 leading-relaxed mb-3">{subPrompt.promptText}</p>

                        {activePromptId === subPrompt.id ? (
                          // Writing mode for sub-prompt
                          <div className="space-y-3">
                            <textarea
                              value={journalText}
                              onChange={(e) => setJournalText(e.target.value)}
                              placeholder="Write your reflection here..."
                              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px] text-gray-700"
                              autoFocus
                            />
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-gray-500">
                                {journalText.length} characters
                              </div>
                              <div className="flex items-center gap-3">
                                {saveMessage && (
                                  <span className={`text-sm ${saveMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                    {saveMessage.text}
                                  </span>
                                )}
                                <button
                                  onClick={handleCancelWriting}
                                  className="px-3 py-1.5 text-gray-600 hover:text-gray-800 text-sm font-medium"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleSaveJournalEntry(subPrompt.id)}
                                  disabled={savingEntry || !journalText.trim()}
                                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                    savingEntry || !journalText.trim()
                                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                      : "bg-blue-600 text-white hover:bg-blue-700"
                                  }`}
                                >
                                  {savingEntry ? "Saving..." : "Save"}
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : subPromptCompleted ? (
                          // Completed state for sub-prompt
                          <div className="space-y-2">
                            <div className="bg-white border border-green-200 rounded-lg p-2">
                              <p className="text-gray-600 text-xs whitespace-pre-wrap line-clamp-3">
                                {getPromptEntry(subPrompt.id)?.response || 'Entry saved'}
                              </p>
                            </div>
                            <button
                              onClick={() => handleStartWriting(subPrompt.id)}
                              className="text-gray-500 hover:text-gray-700 text-xs font-medium flex items-center gap-1"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                              Edit
                            </button>
                          </div>
                        ) : (
                          // View mode for sub-prompt - not completed
                          <button
                            onClick={() => handleStartWriting(subPrompt.id)}
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Add reflection
                          </button>
                        )}
                      </div>
                    </div>
                  )})}
                </div>
              )}

              {/* Weekly Summary - shows progress or completion message */}
              {currentWeekStatus?.isComplete ? (
                // Week completed - show celebration message
                <div className="mt-6 p-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg border border-green-300">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-500 text-white">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-semibold text-green-800">Great job completing Week {currentWeek}!</div>
                      {currentWeek < totalWeeks ? (
                        <p className="text-sm text-green-700 mt-1">
                          {"You've reflected on all prompts for this week. We'll let you know when Week "}{currentWeek + 1}{" is ready!"}
                        </p>
                      ) : (
                        <p className="text-sm text-green-700 mt-1">
                          {"You've completed all available prompts for "}{selectedMeasureData?.name}{". Keep reflecting and check back for new content!"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // Week in progress - show progress
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-blue-700">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-medium">Week {currentWeek} Progress</span>
                    </div>
                    <span className="text-sm font-medium text-blue-600">
                      {Math.round(((currentWeekStatus?.completedCount || 0) / (currentWeekStatus?.totalCount || 1)) * 100)}% complete
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full bg-blue-100 rounded-full h-2 mb-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((currentWeekStatus?.completedCount || 0) / (currentWeekStatus?.totalCount || 1)) * 100}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    Complete all prompts this week to unlock Week {currentWeek + 1} and continue building your {selectedMeasureData?.name?.toLowerCase() || 'skills'}.
                  </p>
                </div>
              )}
            </div>
          ) : (
            // No prompts available for this measure
            <div className="text-center p-8 text-gray-500">
              <p className="font-medium mb-2">No prompts available yet</p>
              <p className="text-sm">New {selectedMeasureData?.name} prompts are being added regularly. Check back soon!</p>
            </div>
          )}
        </div>
      </div>

      {/* My Journal Entries - History Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">My Journal Entries</h3>
        <p className="text-gray-600 mb-6">Review your past reflections and track your growth over time</p>

        {loadingEntries ? (
          <div className="text-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading your entries...</p>
          </div>
        ) : journalEntries.length > 0 ? (
          <div className="space-y-4">
            {journalEntries.map((entry) => (
              <div key={entry.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>{new Date(entry.createdOn).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</span>
                    {entry.timeSpent && (
                      <>
                        <span className="mx-2">•</span>
                        <span>{entry.timeSpent} min</span>
                      </>
                    )}
                  </div>
                  {entry.mood && (
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
                      {entry.mood}
                    </span>
                  )}
                </div>
                <p className="text-gray-700 leading-relaxed line-clamp-3">{entry.response}</p>
                {entry.tags && entry.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {entry.tags.map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center p-8 text-gray-500">
            <p className="font-medium mb-2">No entries yet</p>
            <p className="text-sm">Start writing journal entries above to track your development journey</p>
          </div>
        )}
      </div>
    </div>
  )}

  const renderScenarios = () => (
    <div className="space-y-6">
      {/* Focus Banner */}
      {selectedMeasure && selectedMeasureData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            {getMeasureIconPathByName(selectedMeasureData?.name) ? (
              <img
                src={getMeasureIconPathByName(selectedMeasureData?.name)!}
                alt={selectedMeasureData.name}
                className="w-10 h-10 object-contain"
              />
            ) : (
              <div
                className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center bg-white border border-gray-200"
              />
            )}
            <div>
              <div className="font-semibold text-gray-900">Practice scenarios for: {selectedMeasureData.name}</div>
              <div className="text-sm text-gray-500">Real-world situations to develop your chosen competency</div>
            </div>
          </div>
        </div>
      )}

      {/* Scenario Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Interactive {selectedMeasureData?.name || 'Focus Measure'} Scenarios</h3>
        <p className="text-gray-600">
          Practice your {selectedMeasureData?.name.toLowerCase() || 'chosen competency'} skills with realistic business scenarios. Each scenario provides immediate feedback to help you grow.
        </p>
      </div>

      {/* Current Scenario - Proper three-way conditional */}
      {!selectedMeasure ? (
        // No measure selected
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="text-center p-8 text-gray-500">
            <p className="font-medium mb-2">No focus measure selected</p>
            <p className="text-sm mb-4">Select a focus measure first to see targeted practice scenarios</p>
            <button
              onClick={() => navigate('/microlearning/overview')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Choose Your Focus
            </button>
          </div>
        </div>
      ) : loadingMeasureContent ? (
        // Loading scenarios
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="text-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading scenarios for {selectedMeasureData?.name}...</p>
          </div>
        </div>
      ) : scenarios.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {/* Show first scenario for selected measure */}
          {(() => {
            const focusedScenario = scenarios.find(s => s.measureId === selectedMeasure)

            if (!focusedScenario) {
              return (
                <div className="text-center p-8 text-gray-500">
                  <p className="font-medium mb-2">No scenarios available</p>
                  <p className="text-sm">New {selectedMeasureData?.name} scenarios are being added regularly.</p>
                </div>
              )
            }

            return (
              <>
                <div className="flex items-center gap-3 mb-4">
                  {getMeasureIconPathByName(selectedMeasureData?.name) ? (
                    <img
                      src={getMeasureIconPathByName(selectedMeasureData?.name)!}
                      alt={selectedMeasureData?.name}
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center bg-white border border-gray-200"
                    />
                  )}
                  <div>
                    <div className="font-medium text-gray-900">{selectedMeasureData?.name}</div>
                    <div className="text-sm text-gray-500">Focused Practice Scenario</div>
                  </div>
                </div>

                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  {focusedScenario.title}
                </h4>

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-gray-700 leading-relaxed">
                    {focusedScenario.story}
                  </p>
                </div>

                <div className="space-y-3">
                  <h5 className="font-medium text-gray-900">What would you do?</h5>
                  {focusedScenario.choices.map((choice) => (
                    <div
                      key={choice.id}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedChoice === choice.id
                          ? choice.isOptimal
                            ? "border-green-500 bg-green-50"
                            : "border-red-500 bg-red-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => handleChoiceSelect(choice.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full border-2 border-current flex items-center justify-center text-sm font-bold">
                          {choice.id.toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-900">{choice.text}</p>
                          {showFeedback && selectedChoice === choice.id && (
                            <div className={`mt-3 p-3 rounded-lg ${
                              choice.isOptimal ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"
                            }`}>
                              <p className="font-medium mb-1">{choice.isOptimal ? "Great choice!" : "Consider this"}</p>
                              <p className="text-sm">{choice.feedback}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {showFeedback && (
                  <div className="mt-6">
                    <div className="flex gap-3 mb-4">
                      <button
                        onClick={resetScenario}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Try Again
                      </button>
                    </div>

                    {/* Next scenario timing indicator */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-blue-900">Great job completing this scenario!</p>
                          <p className="text-sm text-blue-700">
                            Your next {selectedMeasureData?.name} scenario will be available next week.
                            New scenarios are delivered bi-weekly to help you practice consistently.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )
          })()
          }
        </div>
      ) : (
        // No scenarios available for this measure
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="text-center p-8 text-gray-500">
            <p className="font-medium mb-2">No scenarios available yet</p>
            <p className="text-sm">New {selectedMeasureData?.name} scenarios are being added regularly. Check back soon!</p>
          </div>
        </div>
      )}
    </div>
  )

  const renderSettings = () => (
    <div className="space-y-6">
      {/* Delivery Preferences Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Delivery Preferences</h3>

        <div className="space-y-4">
          {/* Method Selection */}
          <div className="space-y-2">
            <label className="text-xs text-gray-600 mb-1 block">How to receive</label>
            <div className="flex gap-2">
              {[
                { value: "email", label: "Email" },
                { value: "text", label: "SMS" },
                { value: "portal", label: "Portal" }
              ].map((method) => (
                <button
                  key={method.value}
                  onClick={() => setDeliverySettings(prev => ({ ...prev, method: method.value as DeliveryMethod }))}
                  className={`flex-1 p-2 rounded-lg border-2 text-center text-xs font-medium transition-colors ${
                    deliverySettings.method === method.value
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {method.label}
                </button>
              ))}
            </div>
          </div>

          {/* Email Input - Show when email is selected */}
          {deliverySettings.method === "email" && (
            <div className="space-y-2">
              <label className="text-xs text-gray-600 mb-1 block">Email Address</label>
              <input
                type="email"
                value={deliverySettings.email}
                onChange={(e) => setDeliverySettings(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter your email address"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400 text-xs"
              />
              {deliverySettings.email && !isValidEmail(deliverySettings.email) && (
                <p className="text-xs text-red-500">Please enter a valid email address</p>
              )}
            </div>
          )}

          {/* Phone Number Input - Only show when SMS is selected */}
          {deliverySettings.method === "text" && (
            <div className="space-y-2">
              <label className="text-xs text-gray-600 mb-1 block">Phone Number</label>
              <div className="flex gap-2">
                <select
                  value={deliverySettings.countryCode}
                  onChange={(e) => setDeliverySettings(prev => ({ ...prev, countryCode: e.target.value }))}
                  className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 text-xs"
                >
                  <option value="+1">+1 (US)</option>
                  <option value="+44">+44 (UK)</option>
                  <option value="+61">+61 (AU)</option>
                  <option value="+91">+91 (IN)</option>
                  <option value="+49">+49 (DE)</option>
                  <option value="+33">+33 (FR)</option>
                  <option value="+81">+81 (JP)</option>
                  <option value="+86">+86 (CN)</option>
                </select>
                <input
                  type="tel"
                  value={deliverySettings.phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^\d]/g, '')
                    setDeliverySettings(prev => ({ ...prev, phoneNumber: value }))
                  }}
                  placeholder="Phone number"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400 text-xs"
                  maxLength={15}
                  autoComplete="off"
                  name={`phone-${authStore.user?.id || 'none'}`}
                />
              </div>
              {deliverySettings.phoneNumber && !isValidPhoneNumber(deliverySettings.phoneNumber) && (
                <p className="text-xs text-red-500">Please enter a valid phone number (10-15 digits)</p>
              )}
            </div>
          )}

          {/* Time Selection */}
          <div>
            <label className="text-xs text-gray-600 mb-1 block">When to receive</label>
            <div className="flex gap-2">
              {[
                { value: "morning", label: "Morning", time: "9:00 AM" },
                { value: "afternoon", label: "Afternoon", time: "12:00 PM" },
                { value: "evening", label: "Evening", time: "6:00 PM" }
              ].map((time) => (
                <button
                  key={time.value}
                  onClick={() => setDeliverySettings(prev => ({ ...prev, time: time.value as DeliveryTime }))}
                  className={`flex-1 p-2 rounded-lg border-2 text-center transition-colors ${
                    deliverySettings.time === time.value
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="text-xs font-medium text-gray-900">{time.label}</div>
                  <div className="text-xs text-gray-500">{time.time}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Current Settings Summary */}
          {deliverySettings.method === "text" && !isValidPhoneNumber(deliverySettings.phoneNumber) ? (
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 mt-2">
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Phone Number Required</p>
                <p className="text-xs">Enter a valid phone number to receive SMS notifications</p>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mt-2">
              <div className="text-sm text-gray-700">
                <p className="font-medium mb-1">Current Settings</p>
                <p className="text-xs">
                  Weekly journal prompts via {deliverySettings.method === "text" ? "SMS" : deliverySettings.method} at {deliverySettings.time} time
                  {deliverySettings.method === "text" && deliverySettings.phoneNumber && (
                    <span className="block mt-1">Phone: {deliverySettings.countryCode} {formatPhoneNumber(deliverySettings.phoneNumber)}</span>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Save Button and Status Message */}
          <div className="mt-6 flex items-center gap-4">
            <button
              onClick={handleSavePreferences}
              disabled={savingPreferences || loadingPreferences || (deliverySettings.method === "text" && !isValidPhoneNumber(deliverySettings.phoneNumber))}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                savingPreferences || loadingPreferences || (deliverySettings.method === "text" && !isValidPhoneNumber(deliverySettings.phoneNumber))
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              {savingPreferences ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </span>
              ) : loadingPreferences ? (
                "Loading..."
              ) : (
                "Save Preferences"
              )}
            </button>

            {preferencesMessage && (
              <div className={`text-sm font-medium ${preferencesMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                {preferencesMessage.text}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Measure Choice Section */}
      {selectedMeasure && selectedMeasureData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Focus Measure</h3>
              <p className="text-gray-600 text-sm">Focus on one measure at a time for maximum impact</p>
              <div className="mt-4 flex items-center gap-3">
                <div
                  className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center bg-white border border-gray-200"
                >
                  {getMeasureIconPathByName(selectedMeasureData?.name) ? (
                    <img
                      src={getMeasureIconPathByName(selectedMeasureData?.name)!}
                      alt={selectedMeasureData.name}
                      className="w-6 h-6 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  ) : null}
                </div>
                <span className="text-lg font-medium text-gray-900">
                  {selectedMeasureData.name}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getTierColor(selectedMeasureData.tier ?? '')}`}>
                  {getTierLabel(selectedMeasureData.tier ?? '')}
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowMeasureSelection(!showMeasureSelection)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              {showMeasureSelection ? 'Hide Options' : 'Change Focus'}
            </button>
          </div>

          {(showMeasureSelection || !selectedMeasure) && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-medium text-blue-800 mb-1">Focus Strategy</p>
                <p>Research shows that focusing on one measure at a time leads to 3x better results than trying to improve multiple areas simultaneously.</p>
              </div>
              <div className="grid gap-3">
                {measures.slice(0, 4).map((measure) => (
                  <div
                    key={measure.id}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                      selectedMeasure === measure.id
                        ? "border-blue-500 bg-blue-50 shadow-sm"
                        : measure.tier === "strongest"
                        ? "border-green-300 bg-green-50 hover:border-green-400"
                        : measure.tier === "weakest"
                        ? "border-red-300 bg-red-50 hover:border-red-400"
                        : "border-gray-200 hover:border-gray-300 hover:shadow-md"
                    }`}
                    onClick={() => handleMeasureSelect(measure.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 min-w-0">
                        <div
                          className="w-12 h-12 shrink-0 rounded-full border-4 border-white shadow-sm flex items-center justify-center bg-white"
                        >
                          {getMeasureIconPathByName(measure.name) ? (
                            <img
                              src={getMeasureIconPathByName(measure.name)!}
                              alt={measure.name}
                              className="w-7 h-7 object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none'
                              }}
                            />
                          ) : null}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-semibold text-gray-900 text-sm mb-1">{measure.name}</h4>
                          <p className="text-gray-600 text-xs">{measure.definition.substring(0, 60)}...</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getTierColor(measure.tier ?? '')}`}>
                        {getTierLabel(measure.tier ?? '')}
                      </span>
                    </div>
                  </div>
                ))}
                {measures.length > 4 && (
                  <div className="text-center">
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                      View All {measures.length} Measures →
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )


  // Loading and error states
  if (loading || isLoadingAccessStatus) {
    return (
      <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
        <Sidebar />
        <div className="w-full h-[100dvh] flex-1 flex flex-col">
          <HeaderSection hideBreadcrumbs hideHelpIcon hideProfileIcon />
          <div className="bg-gray-50 relative box-border w-full max-w-[100dvw] h-full flex-1 flex flex-col items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading your learning content...</h2>
              <p className="text-gray-600">Getting your personalized microlearning experience ready</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Block access if results are not approved
  if (!hasResultsAccess) {
    return (
      <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
        <Sidebar />
        <div className="w-full h-[100dvh] flex-1 flex flex-col">
          <HeaderSection hideBreadcrumbs hideHelpIcon hideProfileIcon />
          <div className="bg-gray-50 relative box-border w-full max-w-[100dvw] h-full flex-1 flex flex-col justify-start py-6 px-9">
            <div className="max-w-2xl mx-auto mt-12">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-8 w-8 text-yellow-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                      My Development Not Yet Available
                    </h3>
                    <div className="text-sm text-yellow-700 space-y-2">
                      <p>
                        {accessStatus?.message ||
                          "Your 52-week development path will be available after your organization's simulation results are approved. You'll be notified via email when access is granted."}
                      </p>
                      <p className="mt-4">
                        In the meantime, you can:
                      </p>
                      <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                        <li>View your personal simulation results and insights</li>
                        <li>Review your superpower analysis</li>
                        <li>Access measure guides and definitions</li>
                      </ul>
                      <div className="mt-6">
                        <button
                          onClick={() => navigate("/dashboard/simulations")}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          Go to Simulation Dashboard
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
        <Sidebar />
        <div className="w-full h-[100dvh] flex-1 flex flex-col">
          <HeaderSection hideBreadcrumbs hideHelpIcon hideProfileIcon />
          <div className="bg-gray-50 relative box-border w-full max-w-[100dvw] h-full flex-1 flex flex-col items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to load content</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
      <Sidebar />
      <div className="w-full h-[100dvh] flex-1 flex flex-col">
        <HeaderSection hideBreadcrumbs hideHelpIcon hideProfileIcon />
        <div className="bg-gray-50 relative box-border w-full max-w-[100dvw] h-full flex-1 flex flex-col justify-start py-6 px-9 gap-[19px] text-left text-sm text-gray-600 font-text-sm-medium border-r-[1px] border-solid border-gray-200 overflow-auto">

          {/* Header - Section title based on active section */}
          <div className="w-full mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {activeSection === 'overview' && 'Development Overview'}
              {activeSection === 'journal' && 'Journal'}
              {activeSection === 'scenarios' && 'Practice Scenarios'}
              {activeSection === 'settings' && 'Preferences'}
            </h1>
            <p className="text-gray-500">
              {activeSection === 'overview' && 'Your personalized 52-week development experience designed around your cognitive blueprint'}
              {activeSection === 'journal' && 'Reflect on your growth with guided prompts tailored to your focus measure'}
              {activeSection === 'scenarios' && 'Practice real-world situations to strengthen your cognitive skills'}
              {activeSection === 'settings' && 'Configure your learning preferences and focus measure'}
            </p>
          </div>

          {/* Content */}
          <div className="flex-1">
            {activeSection === 'overview' && renderOverview()}
            {activeSection === 'journal' && renderJournalPrompts()}
            {activeSection === 'scenarios' && renderScenarios()}
            {activeSection === 'settings' && renderSettings()}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Change Your Focus Measure?</h3>
            <p className="text-gray-600 mb-4">
              Changing your focus measure will redirect all your learning content to a new competency.
              Your previous progress will be saved, but your weekly prompts and scenarios will now target the new measure.
            </p>

            {/* Remaining changes warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-amber-800 text-sm font-medium">Limited Changes</p>
                  <p className="text-amber-700 text-sm">
                    You can only change your focus measure 3 times per year.
                    {remainingChanges !== null && remainingChanges > 0 && (
                      <span className="font-medium"> You have {remainingChanges} change{remainingChanges !== 1 ? 's' : ''} remaining this year.</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Error message */}
            {measureChangeError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-700 text-sm">{measureChangeError}</p>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={cancelMeasureChange}
                disabled={isChangingMeasure}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmMeasureChange}
                disabled={isChangingMeasure}
                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {isChangingMeasure && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isChangingMeasure ? 'Changing...' : 'Confirm Change'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MicrolearningDashboard